import { useTheme } from "@emotion/react";
import {
  AttachFileOutlined,
  AudiotrackOutlined,
  CodeOutlined,
  DescriptionOutlined,
  FolderZipOutlined,
  InsertDriveFileOutlined,
  MovieOutlined,
  PersonOutlined,
  PictureAsPdfOutlined,
  PublicOutlined,
  SlideshowOutlined,
  TableChartOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Button,
  CardActionArea,
  Checkbox,
  Chip,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, Box } from "@mui/system";
import { useEffect, useState } from "react";
import api from "../../../services/api";
import UploadAnexo from "../../uploadAnexo/uploadAnexo";
import useStyles from "./styles";

// Mapeamento de extensão -> ícone/cor, para identificar o tipo de arquivo
// nos anexos que não têm preview de imagem.
const FILE_TYPE_BY_EXT = {
  pdf: { icon: PictureAsPdfOutlined, color: "red" },
  doc: { icon: DescriptionOutlined, color: "blue" },
  docx: { icon: DescriptionOutlined, color: "blue" },
  odt: { icon: DescriptionOutlined, color: "blue" },
  txt: { icon: DescriptionOutlined, color: "blue" },
  rtf: { icon: DescriptionOutlined, color: "blue" },
  xls: { icon: TableChartOutlined, color: "green" },
  xlsx: { icon: TableChartOutlined, color: "green" },
  csv: { icon: TableChartOutlined, color: "green" },
  ppt: { icon: SlideshowOutlined, color: "orange" },
  pptx: { icon: SlideshowOutlined, color: "orange" },
  zip: { icon: FolderZipOutlined, color: "purple" },
  rar: { icon: FolderZipOutlined, color: "purple" },
  "7z": { icon: FolderZipOutlined, color: "purple" },
  tar: { icon: FolderZipOutlined, color: "purple" },
  gz: { icon: FolderZipOutlined, color: "purple" },
  mp4: { icon: MovieOutlined, color: "pink" },
  mov: { icon: MovieOutlined, color: "pink" },
  avi: { icon: MovieOutlined, color: "pink" },
  mkv: { icon: MovieOutlined, color: "pink" },
  webm: { icon: MovieOutlined, color: "pink" },
  mp3: { icon: AudiotrackOutlined, color: "yellow" },
  wav: { icon: AudiotrackOutlined, color: "yellow" },
  ogg: { icon: AudiotrackOutlined, color: "yellow" },
  json: { icon: CodeOutlined, color: "gray" },
  xml: { icon: CodeOutlined, color: "gray" },
  html: { icon: CodeOutlined, color: "gray" },
  js: { icon: CodeOutlined, color: "gray" },
};

function getFileTypeMeta(extensao) {
  return (
    FILE_TYPE_BY_EXT[extensao] || {
      icon: InsertDriveFileOutlined,
      color: "gray",
    }
  );
}

export default function AnexosTicketComponent(params) {
  const {
    ticketDetails,
    isEdit = false,
    itensPublic = [],
    setItensPublic = () => {},
    setTicketDetails = () => {},
    isCliente = false,
    canSendAnexo = false,
  } = params;

  const theme = useTheme();
  const styles = useStyles(theme);

  const iconColor = (color, variant = "dark") => {
    if (variant === "dark") {
      return theme.palette.mode === "dark"
        ? theme.palette.colors[color].light
        : theme.palette.colors[color].dark;
    }
    return theme.palette.colors[color][variant];
  };

  const [isSelect, setIsSelect] = useState(false);
  const [selecionados, setSelecionados] = useState(itensPublic.anexos);
  const erp = window?.config?.REACT_APP_ERP || process.env.REACT_APP_ERP;
  const [idItensSelect, setIdItensSelect] = useState([]);
  const [selectItens, setSelectItens] = useState({});
  const [objectItens, setObjectItens] = useState({});
  const [isAddAnexo, setIsAddAnexo] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const imanges = ["jpg", "jpeg", "png", "gif", "bmp", "webp"];

  useEffect(() => {
    resetPublicAnexoClient();
  }, []);

  useEffect(() => {
    setObjectItens(idItensSelect);
  }, [isSelect]);

  useEffect(() => {
    const idsAnexo = Object.entries(selectItens)?.reduce(
      (acc, [key, value]) => {
        if (value === true) {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );

    setIdItensSelect(Object.keys(idsAnexo));
  }, [selectItens]);

  const resetPublicAnexoClient = () => {
    const result = selecionados?.reduce((acc, curr) => {
      acc[curr] = true;
      return acc;
    }, {});

    setSelectItens(result);
    setIsSelect(false);
  };

  const createItensPublic = async (ticketId, itemType, itensId) => {
    try {
      const response = await api.post("/interno/create/itemPublic", {
        ticketId: ticketId,
        itemType: itemType,
        itensId: itensId,
      });
      setSnackbar({
        children: "Itens publicado para o cliente!",
        severity: "success",
      });

      return response?.data;
    } catch (error) {
      console.log(error);
      setSnackbar({
        children: "Error: Não foi possível publicar itens",
        severity: "error",
      });
    } finally {
    }
  };

  const publicAnexoClient = async () => {
    const filtered = Object.entries(selectItens)?.reduce(
      (acc, [key, value]) => {
        if (value === true) {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );
    const arrayItens = Object.keys(filtered);
    try {
      const response = await createItensPublic(
        ticketDetails?.id_atendimento,
        "anexo",
        arrayItens,
      );
      if (!response) {
        setIdItensSelect(objectItens);
        resetPublicAnexoClient();
        return;
      }

      setSelecionados(response);
      setItensPublic((prev) => ({ ...prev, anexos: response }));
    } catch (error) {
      console.error(error);
    } finally {
      setIsSelect(false);
    }
  };

  function formatAnexos(anexo, index) {
    const link = anexo?.link || "";
    const extensao = link?.split(".")?.pop()?.toLowerCase();
    const isImagem = imanges?.includes(extensao);
    const { icon: FileIcon, color: fileColor } = getFileTypeMeta(extensao);

    const isAnexoCliente = itensPublic?.anexosCliente?.some(
      (id) => id?.toString() === anexo?.id_arquivo_upload?.toString(),
    );
    const isChecked =
      !isAnexoCliente && (selectItens[anexo?.id_arquivo_upload] || false);

    const handleChange = (event) => {
      if (isAnexoCliente) return;
      setSelectItens((prev) => ({
        ...prev,
        [anexo?.id_arquivo_upload]: event.target.checked,
      }));
    };

    const statusChip = isAnexoCliente ? (
      <Tooltip title={"Anexo adicionado pelo cliente."}>
        <Chip
          size="small"
          icon={
            <PersonOutlined
              sx={{
                ...styles.chipStatusAnexoIcon,
                fill: theme.palette.white.main,
              }}
            />
          }
          label="Cliente"
          sx={{ ...styles.chipStatusAnexo, ...styles.chipCliente }}
        />
      </Tooltip>
    ) : (
      isChecked &&
      !isSelect && (
        <Tooltip title={"Anexo disponível para visualização do cliente."}>
          <Chip
            size="small"
            icon={
              <PublicOutlined
                sx={{
                  ...styles.chipStatusAnexoIcon,
                  fill: theme.palette.chip.colorText.success,
                }}
              />
            }
            label="Publicado"
            sx={{ ...styles.chipStatusAnexo, ...styles.chipPublicado }}
          />
        </Tooltip>
      )
    );

    return isImagem ? (
      <Tooltip key={index} title={anexo?.nome || ""}>
        <Box sx={styles.containerBoxImg}>
          <Box
            component="img"
            src={link}
            alt={anexo?.nome || `Anexo ${index + 1}`}
            onClick={() =>
              isSelect && !isAnexoCliente
                ? setSelectItens((prev) => ({
                    ...prev,
                    [anexo?.id_arquivo_upload]: true,
                  }))
                : window.open(link, "_blank")
            }
            sx={{ ...styles.boxAnexo }}
          />
          {isChecked && isSelect && !isAnexoCliente && (
            <Box
              onClick={() =>
                setSelectItens((prev) => ({
                  ...prev,
                  [anexo?.id_arquivo_upload]: false,
                }))
              }
              sx={styles.boxFilterColor}
            />
          )}
          {anexo?.nome && (
            <Box sx={styles.imageCaptionOverlay}>
              <Typography sx={styles.imageCaptionText}>
                {anexo?.nome}
              </Typography>
            </Box>
          )}
          {statusChip}
          {isSelect && !isAnexoCliente && (
            <Checkbox
              checked={isChecked}
              onChange={handleChange}
              sx={styles.checkBox}
            />
          )}
        </Box>
      </Tooltip>
    ) : (
      <Tooltip key={index} title={anexo?.nome || ""}>
        <Box sx={{ ...styles.buttonAnexo, position: "relative" }}>
          <CardActionArea
            onClick={() =>
              isSelect && !isAnexoCliente
                ? setSelectItens((prev) => ({
                    ...prev,
                    [anexo?.id_arquivo_upload]: true,
                  }))
                : window.open(link, "_blank")
            }
            sx={{ height: "100%", width: "100%" }}
          >
            <Box sx={styles.containerCard}>
              <Box
                sx={{
                  ...styles.fileIconBadge,
                  backgroundColor: alpha(iconColor(fileColor, "medium"), 0.15),
                }}
              >
                <FileIcon
                  sx={{ fontSize: "24px", color: iconColor(fileColor) }}
                />
              </Box>

              <Box sx={styles.boxTitleAnexo}>
                <Typography
                  sx={{
                    ...styles.boxTextAnexo,
                  }}
                  title={anexo?.nome}
                >
                  {anexo?.nome}
                </Typography>
              </Box>
            </Box>
          </CardActionArea>
          {isChecked && isSelect && !isAnexoCliente && (
            <Box
              onClick={() =>
                setSelectItens((prev) => ({
                  ...prev,
                  [anexo?.id_arquivo_upload]: false,
                }))
              }
              sx={styles.boxFilterColor}
            />
          )}
          {statusChip}
          {isSelect && !isAnexoCliente && (
            <Checkbox
              checked={isChecked}
              onChange={handleChange}
              sx={styles.checkBox}
            />
          )}
        </Box>
      </Tooltip>
    );
  }

  return (
    <>
      <Box sx={styles.boxEdit}>
        <Typography sx={styles.titleDescription}>
          <AttachFileOutlined sx={{ fontSize: "18px" }} />
          Anexos
        </Typography>
        {isEdit && !isAddAnexo && (
          <>
            {isSelect ? (
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  onClick={() => publicAnexoClient()}
                  size="small"
                  variant="contained"
                  /*     disabled={idItensSelect?.length === 0} */
                >
                  Publicar itens ({idItensSelect?.length})
                </Button>
                <Button
                  onClick={() => resetPublicAnexoClient()}
                  size="small"
                  variant="outlined"
                  sx={styles.buttonCancel}
                >
                  Cancelar
                </Button>
              </Box>
            ) : (
              <Button
                onClick={() => setIsSelect(true)}
                size="small"
                variant="outlined"
                sx={styles.buttonCancel}
              >
                Selecionar itens
              </Button>
            )}
          </>
        )}
      </Box>
      <Box sx={styles.containerBodyModal}>
        {!isAddAnexo && (
          <Box sx={styles.boxBodyModal}>
            <Paper
              variant="outlined"
              elevation={0}
              sx={styles.containerPaperModal}
            >
              {ticketDetails?.anexos?.length > 0 ? (
                ticketDetails?.anexos?.map((anexo, index) =>
                  formatAnexos(anexo, index),
                )
              ) : (
                <Typography color="text.secondary" sx={styles.textInfo}>
                  Nenhum anexo disponível
                </Typography>
              )}
            </Paper>
          </Box>
        )}

        {/*    <UploadAnexo /> */}
      </Box>
      {(isEdit || (isCliente && canSendAnexo)) && erp === "HUBSOFT" && (
        <Paper
          elevation={0}
          sx={{
            ...styles.paperNewMensagem,
          }}
        >
          <Box sx={styles.containerNewMensagem}>
            {!isAddAnexo && (
              <Box sx={styles.containerSaveEnviar}>
                <Button
                  onClick={() => setIsAddAnexo(true)}
                  variant="contained"
                  size="small"
                  startIcon={<AttachFileOutlined />}
                >
                  Anexar arquivos
                </Button>
              </Box>
            )}

            {isAddAnexo && (
              <UploadAnexo
                setIsAddAnexo={setIsAddAnexo}
                isAddAnexo={isAddAnexo}
                idAtendimento={ticketDetails?.id_atendimento}
                setTicketDetails={setTicketDetails}
                setSnackbar={setSnackbar}
                isCliente={isCliente}
              />
            )}
          </Box>
          {!!snackbar && (
            <Snackbar
              open
              onClose={() => setSnackbar(null)}
              autoHideDuration={3000}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
              <Alert {...snackbar} onClose={() => setSnackbar(null)} />
            </Snackbar>
          )}
        </Paper>
      )}
    </>
  );
}
